<section
  class="knowledge-settings"
  aria-labelledby="knowledge-settings-heading"
>
  <div class="section-header knowledge-section-header">
    <div>
      <h2 id="knowledge-settings-heading">ナレッジ管理</h2>
      <p>
        ナレッジは、AIがフィードバック草案を作成する際に参照する、状況ごとのアンチパターンとベストプラクティスです。通常のアセスメント評価より踏み込んだ、具体的なフィードバックを可能にします。
      </p>
    </div>
    <button
      class="btn btn-primary toggle-form-button"
      type="button"
      aria-controls="knowledge-form-wrap"
      [attr.aria-expanded]="isFormOpen"
      [disabled]="isSaving"
      (click)="toggleCreateForm()"
    >
      <mat-icon aria-hidden="true">{{ isFormOpen ? 'close' : 'add' }}</mat-icon>
      {{ isFormOpen ? 'フォームを閉じる' : 'ナレッジを追加' }}
    </button>
  </div>

  @if (isFormOpen) {
    <div id="knowledge-form-wrap" class="knowledge-form-wrap">
      <h3>
        {{ editingKnowledgeId == null ? '新しいナレッジ' : 'ナレッジを編集' }}
      </h3>
      <form [formGroup]="knowledgeForm" (ngSubmit)="save()">
        <div class="knowledge-form-grid">
          <div class="knowledge-form-field wide">
            <label class="field-label" for="knowledge-name">ナレッジ名</label>
            <input
              id="knowledge-name"
              class="knowledge-input"
              type="text"
              formControlName="name"
              [attr.maxlength]="nameMaxLength"
              placeholder="例：見積もりだけを希望された時の対応"
            />
            @if (
              knowledgeForm.controls.name.touched &&
              knowledgeForm.controls.name.invalid
            ) {
              <p class="field-error">
                ナレッジ名を{{ nameMaxLength }}文字以内で入力してください。
              </p>
            }
          </div>
          <div class="knowledge-form-field wide">
            <label class="field-label" for="knowledge-situation"
              >具体的な状況</label
            >
            <textarea
              id="knowledge-situation"
              class="knowledge-textarea"
              formControlName="situation"
              [attr.maxlength]="descriptionMaxLength"
              placeholder="どのような状況で参照するナレッジかを入力してください"
            ></textarea>
            @if (
              knowledgeForm.controls.situation.touched &&
              knowledgeForm.controls.situation.invalid
            ) {
              <p class="field-error">
                具体的な状況を{{
                  descriptionMaxLength
                }}文字以内で入力してください。
              </p>
            }
          </div>
          <div class="knowledge-form-field">
            <label class="field-label negative" for="knowledge-bad-response">
              してはいけない対応
            </label>
            <textarea
              id="knowledge-bad-response"
              class="knowledge-textarea"
              formControlName="badResponse"
              [attr.maxlength]="descriptionMaxLength"
              placeholder="避けるべき対応を具体的に入力してください"
            ></textarea>
            @if (
              knowledgeForm.controls.badResponse.touched &&
              knowledgeForm.controls.badResponse.invalid
            ) {
              <p class="field-error">
                してはいけない対応を{{
                  descriptionMaxLength
                }}文字以内で入力してください。
              </p>
            }
          </div>
          <div class="knowledge-form-field">
            <label class="field-label positive" for="knowledge-good-response">
              するべき対応
            </label>
            <textarea
              id="knowledge-good-response"
              class="knowledge-textarea"
              formControlName="goodResponse"
              [attr.maxlength]="descriptionMaxLength"
              placeholder="推奨する対応を具体的に入力してください"
            ></textarea>
            @if (
              knowledgeForm.controls.goodResponse.touched &&
              knowledgeForm.controls.goodResponse.invalid
            ) {
              <p class="field-error">
                するべき対応を{{
                  descriptionMaxLength
                }}文字以内で入力してください。
              </p>
            }
          </div>
          <div class="knowledge-form-field wide">
            <label class="field-label" for="knowledge-reason">理由</label>
            <textarea
              id="knowledge-reason"
              class="knowledge-textarea"
              formControlName="reason"
              [attr.maxlength]="descriptionMaxLength"
              placeholder="なぜその対応が望ましいかを入力してください"
            ></textarea>
            @if (
              knowledgeForm.controls.reason.touched &&
              knowledgeForm.controls.reason.invalid
            ) {
              <p class="field-error">
                理由を{{ descriptionMaxLength }}文字以内で入力してください。
              </p>
            }
          </div>
        </div>
        @if (formErrorMessage) {
          <p class="form-error" role="alert">{{ formErrorMessage }}</p>
        }
        <div class="knowledge-form-actions">
          <button
            class="btn btn-secondary cancel-button"
            type="button"
            [disabled]="isSaving"
            (click)="cancelEditing()"
          >
            キャンセル
          </button>
          <button
            class="btn btn-primary save-button"
            type="submit"
            [disabled]="isSaving"
          >
            @if (isSaving) {
              保存中...
            } @else {
              {{ editingKnowledgeId == null ? '追加' : '更新' }}
            }
          </button>
        </div>
      </form>
    </div>
  }

  @if (isLoading) {
    <div class="status-panel" aria-live="polite">
      <mat-spinner diameter="28" aria-hidden="true"></mat-spinner>
      <span>ナレッジを読み込んでいます。</span>
    </div>
  } @else if (loadErrorMessage) {
    <div class="status-panel error-panel" role="alert">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <span>{{ loadErrorMessage }}</span>
      <button class="btn btn-secondary" type="button" (click)="retry()">
        再試行
      </button>
    </div>
  } @else {
    <div class="knowledge-list-wrap">
      <div class="knowledge-list-head">
        <h3>登録済みナレッジ</h3>
        <span class="count-chip">{{ countText }}</span>
      </div>
      <label class="knowledge-search">
        <mat-icon aria-hidden="true">search</mat-icon>
        <span class="sr-only">登録済みナレッジを検索</span>
        <input
          class="knowledge-input"
          type="search"
          [formControl]="searchControl"
          placeholder="ナレッジ名・状況・対応例・理由を検索"
          autocomplete="off"
        />
      </label>

      @if (deleteErrorMessage) {
        <p class="delete-error" role="alert">{{ deleteErrorMessage }}</p>
      }

      @if (knowledges.length === 0) {
        <div class="knowledge-empty">
          登録済みのナレッジはありません。「ナレッジを追加」から登録してください。
        </div>
      } @else if (filteredKnowledges.length === 0 && isSearching) {
        <div class="knowledge-empty">該当するナレッジがありません。</div>
      } @else {
        <div class="knowledge-list">
          @for (knowledge of filteredKnowledges; track knowledge.id) {
            <article
              class="knowledge-item"
              [attr.data-knowledge-id]="knowledge.id"
            >
              <div class="knowledge-item-head">
                <h4>{{ knowledge.name }}</h4>
                <div class="knowledge-item-actions">
                  <button
                    class="knowledge-edit"
                    type="button"
                    [attr.aria-label]="knowledge.name + 'を編集'"
                    [disabled]="isSaving || deletingKnowledgeId != null"
                    (click)="startEditing(knowledge)"
                  >
                    <mat-icon aria-hidden="true">edit</mat-icon>
                    編集
                  </button>
                  <button
                    class="knowledge-delete"
                    type="button"
                    [attr.aria-label]="knowledge.name + 'を削除'"
                    [disabled]="isSaving || deletingKnowledgeId != null"
                    (click)="requestDelete(knowledge)"
                  >
                    <mat-icon aria-hidden="true">delete</mat-icon>
                    {{
                      deletingKnowledgeId === knowledge.id
                        ? '削除中...'
                        : '削除'
                    }}
                  </button>
                </div>
              </div>
              <div class="knowledge-situation">
                <div class="knowledge-label">具体的な状況</div>
                <p>{{ knowledge.situation }}</p>
              </div>
              <div class="knowledge-comparison">
                <div class="knowledge-guidance bad">
                  <div class="knowledge-label">
                    <mat-icon aria-hidden="true">close</mat-icon>
                    してはいけない対応
                  </div>
                  <p>{{ knowledge.badResponse }}</p>
                </div>
                <div class="knowledge-guidance good">
                  <div class="knowledge-label">
                    <mat-icon aria-hidden="true">check</mat-icon>
                    するべき対応
                  </div>
                  <p>{{ knowledge.goodResponse }}</p>
                </div>
              </div>
              <div class="knowledge-reason">
                <div class="knowledge-label">理由</div>
                <p>{{ knowledge.reason }}</p>
              </div>
            </article>
          }
        </div>
      }
    </div>
  }
</section>
